// src/store/useAuthStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { axiosInstance, axiosInstancev1 } from "../lib/axiosInstance";

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      userType: null,
      token: null,
      isSigningUp: false,
      isCheckingAuth: false,

      // ===========================================================
      // BUILDER SIGNUP
      // ===========================================================
      signupBuilder: async (builderData) => {
        set({ isSigningUp: true });
        try {
          const formData = new FormData();

          Object.keys(builderData).forEach((key) => {
            if (builderData[key] !== null && builderData[key] !== undefined) {
              if (key === "verifiedAadhaarData" || key === "verifiedGstinData") {
                formData.append(key, JSON.stringify(builderData[key]));
              } else if (
                key === "selectedServiceAreas" &&
                Array.isArray(builderData[key]) &&
                builderData[key].length > 0
              ) {
                formData.append("selectedTalukIds", JSON.stringify(builderData[key]));
              } else if (key !== "selectedServiceAreas") {
                formData.append(key, builderData[key]);
              }
            }
          });

          await axiosInstance.post("/builders/auth/signup", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          return { success: true, message: "Account created successfully. Please login." };

        } catch (error) {
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Signup failed";

          toast.error(errorMsg);
          return { success: false, message: errorMsg };

        } finally {
          set({ isSigningUp: false });
        }
      },

      // ===========================================================
      // BUILDER LOGIN (FULLY FIXED FOR LIVE)
      // ===========================================================
      loginBuilder: async (credential) => {
        try {
          const response = await axiosInstance.post("/builders/auth/login", credential);
          const token = response.data.token;

          // Save token
          localStorage.setItem("builderToken", token);

          // Decode JWT
          const payload = JSON.parse(atob(token.split(".")[1]));

          // Handle Project Incharge login (role: 'incharge')
          if (payload.role === "incharge") {
            const parseAssigned = (raw) => {
              if (!raw) return [];
              if (Array.isArray(raw)) return raw.map(Number).filter((n) => !Number.isNaN(n) && n > 0);
              if (typeof raw === "string") {
                try {
                  const parsed = JSON.parse(raw);
                  return Array.isArray(parsed) ? parsed.map(Number).filter((n) => !Number.isNaN(n) && n > 0) : [];
                } catch {
                  return [];
                }
              }
              return [];
            };

            const inchargeData = {
              // CRITICAL: id must be builder_profile_id so all builder API calls work
              id: payload.builder_profile_id,
              builder_profile_id: payload.builder_profile_id,
              builderId: payload.builder_profile_id,
              builder_id: payload.builder_profile_id,

              // Unique Ducktail ID for ranking/support lookups
              ducktail_id: payload.ducktail_id,
              builderUniqueId: payload.ducktail_id,

              // Incharge-specific IDs
              inchargeId: payload.user_id,

              email: payload.email,
              name: payload.name,
              role: "incharge",         // keep role for sidebar/layout differentiation
              assignedProjects: parseAssigned(payload.assigned_projects),
              assigned_projects: parseAssigned(payload.assigned_projects),
              permissions: payload.permissions || [],
            };

            set({
              user: inchargeData,
              userType: "builder",   // treat as builder so all builder stores work
              token: token,
            });

            toast.success("Login Successful");
            return true;
          }

          // 🔥 Unified builder object (all keys supported)
          const userData = {
            // Primary LIVE backend fields
            id: payload.builder_profile_id,
            builder_profile_id: payload.builder_profile_id,
            builderId: payload.builder_profile_id,
            builder_id: payload.builder_profile_id,

            // Unique Ducktail ID
            ducktail_id: payload.ducktail_id,
            builderUniqueId: payload.ducktail_id,

            // User table ID
            user_id: payload.user_id,

            // Other fields
            email: payload.email,
            name: payload.name,
            role: payload.role,
          };

          // Save into Zustand
          set({
            user: userData,
            userType: "builder",
            token: token,
          });

          toast.success("Builder Login Successfully");
          return true;

        } catch (error) {
          const errorMsg = error.response?.data?.message || "Builder Login Failed";
          toast.error(errorMsg);
          throw error;
        }
      },

      // ===========================================================
      // CUSTOMER SIGNUP
      // ===========================================================
      customerSignup: async (name, email, password, otp) => {
        try {
          const response = await axiosInstance.post("/customer/signup", {
            name,
            email,
            password,
            otp,
          });

          if (response.data.success && response.data.token) {
            const token = response.data.token;
            localStorage.setItem("customerToken", token);

            const payload = JSON.parse(atob(token.split(".")[1]));

            const userData =
              response.data.user || {
                name,
                email,
                customerId: payload.customerId,
                userId: payload.userId,
              };

            set({
              user: {
                name: userData.name,
                email: userData.email,
                customerId: payload.customerId,
                userId: payload.userId,
              },
              userType: "customer",
              token: token,
            });

            toast.success("Signup successful & logged in!");
            return true;
          }

          toast.success("Customer Signup Successful! Please login.");
          return { registeredOnly: true };

        } catch (error) {
          const errorMsg = error.response?.data?.message || "Customer Signup Failed";
          toast.error(errorMsg);
          return false;
        }
      },

      // ===========================================================
      // AGENT LOGIN
      // ===========================================================
      agentLogin: async (credential) => {
        try {
          const response = await axiosInstancev1.post("/real-estate-agents/auth/login", credential);
          const token = response.data.token;
          const agentData = response.data.user;

          localStorage.setItem("agentToken", token);

          set({
            user: agentData,
            userType: "agent",
            token: token,
          });

          toast.success("Agent Login Successful");
          return true;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Agent Login Failed";
          toast.error(errorMsg);
          throw error;
        }
      },

      // ===========================================================
      // AGENT SIGNUP
      // ===========================================================
      agentSignup: async (agentData) => {
        set({ isSigningUp: true });
        try {
          const response = await axiosInstancev1.post("/real-estate-agents/auth/signup", agentData);

          // Backend returns token and user directly on successful signup
          if (response.data.token && response.data.user) {
            toast.success(response.data.message || "Agent Signup Successful! Please login.");
            return true;
          }
          return false;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Agent Signup Failed";
          toast.error(errorMsg);
          return false;
        } finally {
          set({ isSigningUp: false });
        }
      },

      // ===========================================================
      // CUSTOMER LOGIN
      // ===========================================================
      customerLogin: async (credential) => {
        try {
          const response = await axiosInstance.post("/customer/login", credential);
          const token = response.data.token;

          localStorage.setItem("customerToken", token);
          const payload = JSON.parse(atob(token.split(".")[1]));

          set({
            user: {
              name: response.data.name,
              email: response.data.email,
              customerId: payload.customerId,
              userId: payload.userId,
            },
            userType: "customer",
            token: token,
          });

          toast.success("Customer Login Successfully");
          return true;

        } catch (error) {
          const errorMsg = error.response?.data?.message || "Customer Login Failed";
          toast.error(errorMsg);
          throw error;
        }
      },

      // ===========================================================
      // GOOGLE LOGIN
      // ===========================================================
      setGoogleAuth: (userData, token) => {
        localStorage.setItem("customerToken", token);
        sessionStorage.setItem("googleAuthStatus", "success");

        set({
          user: {
            name: userData.name,
            email: userData.email,
            customerId: userData.customerId,
            userId: userData.id,
          },
          userType: "customer",
          token: token,
        });

        toast.success("Logged in with Google successfully!");
      },

      // ===========================================================
      // LOGOUT
      // ===========================================================
      logout: async (userTypeName) => {
        localStorage.clear();
        sessionStorage.clear();

        const cookies = document.cookie.split(";");
        cookies.forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });

        set({ user: null, userType: null, token: null });

        // Also clear specialized stores if they exist and are loaded
        try {
          // Dynamic import or check if store is already in memory
          // Since we might have a circular dependency if we import at top level, 
          // we can try to access the specialized store's setState if it's been initialized
          const consultantStore = (await import("./consultant/useConsultantAuthStore")).default;
          consultantStore.setState({ consultant: null, token: null });
        } catch (err) {
          // If the store isn't available or fails to import, it's likely not being used/persisted yet
        }

        toast.success(`${userTypeName} logged out.`);
      },

      logoutBuilder: () => {
        if (get().userType === "builder") get().logout("Builder");
      },

      logoutAgent: () => {
        if (get().userType === "agent") get().logout("Agent");
      },

      logoutCustomer: async () => {
        if (get().userType === "customer") {
          try {
            const mod = await import("./customer/useProfileStore");
            const useCustomerProfileStore = mod.default;

            const customerState = useCustomerProfileStore.getState();
            if (customerState?.reset) customerState.reset();

            get().logout("Customer");

          } catch {
            get().logout("Customer");
          }
        }
      },

      logoutConsultant: () => {
        if (get().userType === "consultant") {
          // Import greedily or use the specialized store if available
          // For now, simple global cleanup is sufficient since specialized store clears itself
          get().logout("Consultant");
        }
      },

      // ===========================================================
      // PASSWORD RESET (Builder & Customer)
      // ===========================================================
      forgotPassword: async (email) => {
        try {
          const res = await axiosInstance.post("/builders/auth/forgot-password", { email });
          return res.data.success;
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to send reset email.");
          return false;
        }
      },

      verifyResetToken: async (token) => {
        try {
          const res = await axiosInstance.get(`/builders/auth/verify-reset-token/${token}`);
          return res.data.success;
        } catch {
          return false;
        }
      },

      resetPassword: async (token, newPassword) => {
        try {
          const res = await axiosInstance.post("/builders/auth/reset-password", {
            token,
            newPassword,
          });
          return res.data.success;
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to reset password.");
          return false;
        }
      },

      customerForgotPassword: async (email) => {
        try {
          const res = await axiosInstance.post("/customer/forgot-password", { email });
          return res.data.success;
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to send reset email.");
          return false;
        }
      },

      customerVerifyResetToken: async (token) => {
        try {
          const res = await axiosInstance.get(`/customer/verify-reset-token/${token}`);
          return res.data.success;
        } catch {
          return false;
        }
      },

      customerResetPassword: async (token, newPassword) => {
        try {
          const res = await axiosInstance.post("/customer/reset-password", {
            token,
            newPassword,
          });
          return res.data.success;
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to reset password.");
          return false;
        }
      },
    }),

    {
      name: "auth-storage",
      getStorage: () => localStorage,
    }
  )
);

export default useAuthStore;
